import React, { memo } from 'react';
import { ClipboardCheckIcon, BookOpenIcon, TrophyIcon, CheckIcon, LockIcon, ShieldIcon, WifiIcon, SmartphoneIcon, CreditCardIcon, EyeOffIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { TopBar } from '../components/TopBar';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
export function HowItWorksPage() {
  return <div className="flex flex-col min-h-screen w-full" data-id="element-1326">
      <TopBar data-id="element-1327" />
      <Header data-id="element-1328" />

      {/* Hero Section */}
      <section className="w-full bg-white py-20 md:py-28 border-b border-gray-200" data-id="element-1329">
        <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-1330">
          <div className="max-w-4xl mx-auto text-center" data-id="element-1331">
            <h1 className="mb-6 text-5xl md:text-6xl font-extrabold text-[#111111] leading-tight" data-id="element-1332">
              How Caveo Works
            </h1>
            <p className="text-xl md:text-2xl text-gray-600 leading-relaxed" data-id="element-1333">
              A simple 3-level education system that builds real digital
              confidence—at your own pace, in plain English.
            </p>
          </div>
        </div>
      </section>

      {/* The 3-Step Journey */}
      <section className="w-full bg-[#f8f8f8] py-16 md:py-24" data-id="element-1334">
        <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-1335">
          <div className="text-center mb-16" data-id="element-1336">
            <h2 className="text-3xl md:text-4xl font-bold text-[#111111] mb-4" data-id="element-1337">
              Your journey to digital confidence
            </h2>
            <p className="text-lg text-gray-600" data-id="element-1338">
              Three simple steps. No tech degree required.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12" data-id="element-1339">
            <div className="bg-white rounded-2xl p-8 border-2 border-gray-200 text-center relative" data-id="element-1340">
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-[#631bb7] text-white w-10 h-10 rounded-full flex items-center justify-center font-bold" data-id="element-1341">
                1
              </div>
              <div className="w-20 h-20 bg-[#631bb7] bg-opacity-5 rounded-2xl flex items-center justify-center mx-auto mb-6 border border-[#631bb7] border-opacity-10" data-id="element-1342">
                <ClipboardCheckIcon className="w-10 h-10 text-[#631bb7]" data-id="element-1343" />
              </div>
              <h3 className="text-2xl font-bold text-[#111111] mb-4" data-id="element-1344">Assess</h3>
              <p className="text-gray-600 leading-relaxed mb-4" data-id="element-1345">
                Answer simple questions about your devices, habits, and
                concerns. In under 3 minutes, we'll create a personalized
                security plan.
              </p>
              <ul className="text-left space-y-2" data-id="element-1346">
                <li className="flex items-center text-sm text-gray-500" data-id="element-1347">
                  <CheckIcon className="w-4 h-4 text-[#631bb7] mr-2" data-id="element-1348" />
                  No technical knowledge needed
                </li>
                <li className="flex items-center text-sm text-gray-500" data-id="element-1349">
                  <CheckIcon className="w-4 h-4 text-[#631bb7] mr-2" data-id="element-1350" />
                  Skip questions that don't apply
                </li>
                <li className="flex items-center text-sm text-gray-500" data-id="element-1351">
                  <CheckIcon className="w-4 h-4 text-[#631bb7] mr-2" data-id="element-1352" />
                  Get your Security Score instantly
                </li>
              </ul>
            </div>

            <div className="bg-white rounded-2xl p-8 border-2 border-gray-200 text-center relative" data-id="element-1353">
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-[#631bb7] text-white w-10 h-10 rounded-full flex items-center justify-center font-bold" data-id="element-1354">
                2
              </div>
              <div className="w-20 h-20 bg-[#631bb7] bg-opacity-5 rounded-2xl flex items-center justify-center mx-auto mb-6 border border-[#631bb7] border-opacity-10" data-id="element-1355">
                <BookOpenIcon className="w-10 h-10 text-[#631bb7]" data-id="element-1356" />
              </div>
              <h3 className="text-2xl font-bold text-[#111111] mb-4" data-id="element-1357">Guide</h3>
              <p className="text-gray-600 leading-relaxed mb-4" data-id="element-1358">
                Follow bite-sized lessons with clear visuals and step-by-step
                instructions. Learn at your own pace—no pressure.
              </p>
              <ul className="text-left space-y-2" data-id="element-1359">
                <li className="flex items-center text-sm text-gray-500" data-id="element-1360">
                  <CheckIcon className="w-4 h-4 text-[#631bb7] mr-2" data-id="element-1361" />
                  Plain English, no jargon
                </li>
                <li className="flex items-center text-sm text-gray-500" data-id="element-1362">
                  <CheckIcon className="w-4 h-4 text-[#631bb7] mr-2" data-id="element-1363" />
                  Visual guides show exactly what to do
                </li>
                <li className="flex items-center text-sm text-gray-500" data-id="element-1364">
                  <CheckIcon className="w-4 h-4 text-[#631bb7] mr-2" data-id="element-1365" />
                  Pause and resume anytime
                </li>
              </ul>
            </div>

            <div className="bg-white rounded-2xl p-8 border-2 border-gray-200 text-center relative" data-id="element-1366">
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-[#631bb7] text-white w-10 h-10 rounded-full flex items-center justify-center font-bold" data-id="element-1367">
                3
              </div>
              <div className="w-20 h-20 bg-[#631bb7] bg-opacity-5 rounded-2xl flex items-center justify-center mx-auto mb-6 border border-[#631bb7] border-opacity-10" data-id="element-1368">
                <TrophyIcon className="w-10 h-10 text-[#631bb7]" data-id="element-1369" />
              </div>
              <h3 className="text-2xl font-bold text-[#111111] mb-4" data-id="element-1370">
                Empower
              </h3>
              <p className="text-gray-600 leading-relaxed mb-4" data-id="element-1371">
                Track your progress, celebrate wins, and watch your confidence
                grow. See your Security Score improve over time.
              </p>
              <ul className="text-left space-y-2" data-id="element-1372">
                <li className="flex items-center text-sm text-gray-500" data-id="element-1373">
                  <CheckIcon className="w-4 h-4 text-[#631bb7] mr-2" data-id="element-1374" />
                  Family dashboard shows everyone's progress
                </li>
                <li className="flex items-center text-sm text-gray-500" data-id="element-1375">
                  <CheckIcon className="w-4 h-4 text-[#631bb7] mr-2" data-id="element-1376" />
                  Celebrate milestones together
                </li>
                <li className="flex items-center text-sm text-gray-500" data-id="element-1377">
                  <CheckIcon className="w-4 h-4 text-[#631bb7] mr-2" data-id="element-1378" />
                  Build lasting independence
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* The 3-Level Education System */}
      <section className="w-full bg-white py-16 md:py-24 border-t border-gray-200" data-id="element-1379">
        <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-1380">
          <div className="text-center mb-16" data-id="element-1381">
            <h2 className="text-3xl md:text-4xl font-bold text-[#111111] mb-4" data-id="element-1382">
              Our 3-Level Education System
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto" data-id="element-1383">
              39 modules across three skill levels. Start where you are, grow at
              your pace.
            </p>
          </div>

          {/* Level 1 */}
          <div className="mb-12" data-id="element-1384">
            <div className="flex items-center mb-6" data-id="element-1385">
              <div className="bg-green-100 text-green-700 px-4 py-2 rounded-lg font-bold text-sm uppercase tracking-wide mr-4" data-id="element-1386">
                Level 1
              </div>
              <h3 className="text-2xl font-bold text-[#111111]" data-id="element-1387">Essentials</h3>
              <span className="ml-auto text-gray-500 font-medium" data-id="element-1388">
                9 Modules
              </span>
            </div>
            <p className="text-gray-600 mb-6" data-id="element-1389">
              The fundamentals everyone needs. Perfect for beginners or anyone
              wanting to solidify their basics.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-1390">
              {[{
              icon: LockIcon,
              title: 'Creating Bulletproof Passwords',
              desc: 'Learn to create and manage strong passwords without memorizing them all'
            }, {
              icon: ShieldIcon,
              title: 'Recognizing Scams & Phishing',
              desc: 'Spot fake emails, texts, and calls before they trick you'
            }, {
              icon: WifiIcon,
              title: 'Securing Your Home WiFi',
              desc: 'Protect your network from unwanted visitors'
            }].map((module, index) => <div key={index} className="bg-[#f8f8f8] rounded-xl p-5 border-2 border-gray-200" data-id="element-1391">
                  <module.icon className="w-6 h-6 text-green-600 mb-3" data-id="element-1392" />
                  <h4 className="font-bold text-[#111111] mb-2" data-id="element-1393">
                    {module.title}
                  </h4>
                  <p className="text-sm text-gray-600" data-id="element-1394">{module.desc}</p>
                </div>)}
            </div>
          </div>

          {/* Level 2 */}
          <div className="mb-12" data-id="element-1395">
            <div className="flex items-center mb-6" data-id="element-1396">
              <div className="bg-blue-100 text-blue-700 px-4 py-2 rounded-lg font-bold text-sm uppercase tracking-wide mr-4" data-id="element-1397">
                Level 2
              </div>
              <h3 className="text-2xl font-bold text-[#111111]" data-id="element-1398">
                Intermediate
              </h3>
              <span className="ml-auto text-gray-500 font-medium" data-id="element-1399">
                11 Modules
              </span>
            </div>
            <p className="text-gray-600 mb-6" data-id="element-1400">
              Build on your foundation with more advanced protection strategies.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-1401">
              {[{
              icon: CreditCardIcon,
              title: 'Protecting Financial Accounts',
              desc: 'Secure your bank accounts, credit cards, and investment apps'
            }, {
              icon: SmartphoneIcon,
              title: 'Mobile Device Security',
              desc: 'Keep your phone and tablet safe from threats'
            }, {
              icon: EyeOffIcon,
              title: 'Privacy Settings Deep Dive',
              desc: 'Take control of what apps and websites know about you'
            }].map((module, index) => <div key={index} className="bg-[#f8f8f8] rounded-xl p-5 border-2 border-gray-200" data-id="element-1402">
                  <module.icon className="w-6 h-6 text-blue-600 mb-3" data-id="element-1403" />
                  <h4 className="font-bold text-[#111111] mb-2" data-id="element-1404">
                    {module.title}
                  </h4>
                  <p className="text-sm text-gray-600" data-id="element-1405">{module.desc}</p>
                </div>)}
            </div>
          </div>

          {/* Level 3 */}
          <div data-id="element-1406">
            <div className="flex items-center mb-6" data-id="element-1407">
              <div className="bg-[#631bb7] bg-opacity-20 text-[#631bb7] px-4 py-2 rounded-lg font-bold text-sm uppercase tracking-wide mr-4" data-id="element-1408">
                Level 3
              </div>
              <h3 className="text-2xl font-bold text-[#111111]" data-id="element-1409">
                Specialized Guides
              </h3>
              <span className="ml-auto text-gray-500 font-medium" data-id="element-1410">
                19 Deep Dives
              </span>
            </div>
            <p className="text-gray-600 mb-6" data-id="element-1411">
              Expert-level topics for those who want comprehensive protection.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-id="element-1412">
              {['AI Safety Primer', 'Digital Legacy Planning', 'Identity Masking', 'Cloud Storage Security', 'Smart Home Protection', 'Travel Security', 'Social Media Privacy', 'Data Broker Removal'].map((topic, index) => <div key={index} className="bg-[#f8f8f8] rounded-lg p-4 border border-gray-200 text-center" data-id="element-1413">
                  <span className="text-sm font-medium text-[#111111]" data-id="element-1414">
                    {topic}
                  </span>
                </div>)}
            </div>
          </div>
        </div>
      </section>

      {/* Sample Module Preview */}
      <section className="w-full bg-[#f8f8f8] py-16 md:py-24 border-t border-gray-200" data-id="element-1415">
        <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-1416">
          <div className="text-center mb-12" data-id="element-1417">
            <h2 className="text-3xl md:text-4xl font-bold text-[#111111] mb-4" data-id="element-1418">
              See what a lesson looks like
            </h2>
            <p className="text-lg text-gray-600" data-id="element-1419">
              Clear instructions. Visual guides. No confusion.
            </p>
          </div>

          <div className="max-w-4xl mx-auto bg-white rounded-2xl border-2 border-gray-200 overflow-hidden" data-id="element-1420">
            {/* Module Header */}
            <div className="bg-[#1a0647] p-6" data-id="element-1421">
              <div className="flex items-center justify-between" data-id="element-1422">
                <div data-id="element-1423">
                  <span className="text-[#00f4f8] text-sm font-semibold" data-id="element-1424">
                    Level 1 • Module 2
                  </span>
                  <h3 className="text-white text-xl font-bold mt-1" data-id="element-1425">
                    Recognizing Scams & Phishing
                  </h3>
                </div>
                <div className="text-white text-right" data-id="element-1426">
                  <span className="text-sm opacity-70" data-id="element-1427">Progress</span>
                  <div className="text-lg font-bold" data-id="element-1428">3 of 8 steps</div>
                </div>
              </div>
              <div className="mt-4 bg-white bg-opacity-20 rounded-full h-2" data-id="element-1429">
                <div className="bg-[#00f4f8] h-2 rounded-full" style={{
                width: '37.5%'
              }} data-id="element-1430"></div>
              </div>
            </div>

            {/* Module Content */}
            <div className="p-8" data-id="element-1431">
              <h4 className="text-2xl font-bold text-[#111111] mb-4" data-id="element-1432">
                Step 3: Check the sender's email address
              </h4>
              <p className="text-gray-600 mb-6 leading-relaxed" data-id="element-1433">
                Scammers often use email addresses that look similar to real
                companies but have small differences. Here's how to spot them:
              </p>

              {/* Visual Example */}
              <div className="bg-[#f8f8f8] rounded-xl p-6 mb-6 border-2 border-gray-200" data-id="element-1434">
                <p className="text-sm font-semibold text-gray-500 mb-4" data-id="element-1435">
                  EXAMPLE: Spot the fake
                </p>
                <div className="space-y-3" data-id="element-1436">
                  <div className="flex items-center bg-white rounded-lg p-4 border-2 border-green-200" data-id="element-1437">
                    <CheckIcon className="w-5 h-5 text-green-500 mr-3" data-id="element-1438" />
                    <span className="font-mono text-sm" data-id="element-1439">
                      support@amazon.com
                    </span>
                    <span className="ml-auto text-green-600 text-sm font-medium" data-id="element-1440">
                      Real
                    </span>
                  </div>
                  <div className="flex items-center bg-white rounded-lg p-4 border-2 border-red-200" data-id="element-1441">
                    <span className="text-red-500 mr-3" data-id="element-1442">✗</span>
                    <span className="font-mono text-sm" data-id="element-1443">
                      support@amaz0n-secure.com
                    </span>
                    <span className="ml-auto text-red-600 text-sm font-medium" data-id="element-1444">
                      Fake
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between" data-id="element-1445">
                <button className="text-gray-500 font-medium hover:text-[#631bb7] transition-colors" data-id="element-1446">
                  ← Previous Step
                </button>
                <button className="bg-[#631bb7] text-white px-6 py-3 rounded-lg font-bold hover:bg-[#521099] transition-colors" data-id="element-1447">
                  Next Step →
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="w-full bg-white py-16 md:py-24 border-t border-gray-200" data-id="element-1448">
        <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-1449">
          <div className="max-w-3xl mx-auto text-center" data-id="element-1450">
            <h2 className="text-3xl md:text-4xl font-bold text-[#111111] mb-4" data-id="element-1451">
              Ready to start learning?
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed mb-8" data-id="element-1452">
              Take your free assessment and get your personalized learning path
              in under 3 minutes.
            </p>
            <Link to="/intro-assessment" className="bg-[#631bb7] text-white px-8 py-4 rounded-lg font-bold inline-flex items-center justify-center no-underline hover:bg-[#521099] transition-all shadow-md" data-id="element-1453">
              Start My Assessment
            </Link>
          </div>
        </div>
      </section>

      <Footer data-id="element-1454" />
    </div>;
}